// Tutor review state, derived from the tutor's issues across all time.
//
// There is no separate tutor status column: a tutor is "under review" when any
// of their issues is under review, and "resolved" once every issue they have
// had is closed. A new open issue moves them back out of "resolved", so active
// and historical concerns stay distinct while the issue history is kept.
//
// Uses the same countable-issue rules as every other surface
// (lib/issueCounting.js): no lateness, no guardrail-suppressed risks.

import { supabase } from './supabase';
import { ISSUE_SELECT_COLUMNS, LATENESS_CATEGORY, countableIssues, bucketForStatus } from './issueCounting';

export const TUTOR_REVIEW_LABELS = {
  under_review: 'Under review',
  resolved: 'Resolved',
  open: 'Open issues',
};

// 'under_review' | 'open' | 'resolved' | null (no issues)
export const tutorReviewState = (issues) => {
  if (!issues?.length) return null;
  const buckets = new Set(issues.map((r) => bucketForStatus(r.status)));
  if (buckets.has('under_review')) return 'under_review';
  if (buckets.has('open')) return 'open';
  return 'resolved';
};

const PAGE_SIZE = 1000;
const MAX_PAGES = 50;

// { [tutor_name]: state } for every tutor in the org with at least one issue.
export const loadTutorReviewStates = async (organizationId, policy) => {
  if (!organizationId) return {};
  const rows = [];
  for (let p = 0; p < MAX_PAGES; p++) {
    const from = p * PAGE_SIZE;
    const { data, error } = await supabase
      .from('risks_v2')
      .select(`${ISSUE_SELECT_COLUMNS}, sessions!inner(tutor_name)`)
      .eq('organization_id', organizationId)
      .neq('category', LATENESS_CATEGORY)
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) { console.error('tutor review states failed:', error); break; }
    if (!data) break;
    rows.push(...data);
    if (data.length < PAGE_SIZE) break;
  }

  const byTutor = {};
  countableIssues(rows, policy).forEach((r) => {
    const name = r.sessions?.tutor_name;
    if (!name) return;
    if (!byTutor[name]) byTutor[name] = [];
    byTutor[name].push(r);
  });

  const states = {};
  Object.entries(byTutor).forEach(([name, issues]) => { states[name] = tutorReviewState(issues); });
  return states;
};
